import { configs } from '../configs';
import { authenticatedFetch } from '../utils/authenticatedFetch';

export interface Bonus {
  id: string;
  name: string;
  description?: string;
  entityType: 'shift' | 'attendance' | 'performance' | 'other';
  position?: string;
  staffingType?: 'full-time' | 'part-time' | 'contract';
  timeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night' | 'custom';
  customTimeRange?: {
    startTime: string;
    endTime: string;
  };
  dayOfWeek?: number[];
  bonusAmount: number;
  bonusCurrency: string;
  isActive: boolean;
  conditions?: Record<string, any>;
  createdBy: string;
  tenantId: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface BonusCreateEntry extends Omit<Bonus, 'id' | 'createdAt' | 'updatedAt'> {}
export interface BonusUpdateEntry extends Partial<BonusCreateEntry> {}

class BonusService {
  private apiRoot = configs.apiRoot;

  async getBonuses(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
    q?: string;
  }): Promise<Bonus[] & { __contentRange?: string; __total?: number }> {
    try {
      const queryParams = new URLSearchParams();

      const page = params?.page ?? 1;
      let size = 20;

      if (params?.size) {
        size = params.size;
      } else if (params?.perPage) {
        size = params.perPage;
      }

      queryParams.append('page', page.toString());
      queryParams.append('size', size.toString());

      if (params?.q) queryParams.append('q', params.q);
      if (params?.filter)
        queryParams.append('filter', JSON.stringify(params.filter));
      if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

      const url = `${this.apiRoot}/bonuses${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;

      const response = await authenticatedFetch(url, { method: 'GET' });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        throw new Error(
          error.message || `Failed to fetch bonuses: ${response.statusText}`
        );
      }

      const result = await response.json();

      // Check Content-Range header for total count
      const contentRange = response.headers.get('Content-Range');
      if (contentRange) {
        const totalMatch = contentRange.match(/\/(\d+)/);
        if (totalMatch) {
          (result as any).__total = parseInt(totalMatch[1], 10);
        }
      }

      return result;
    } catch (error) {
      console.error('Error fetching bonuses:', error);
      throw error;
    }
  }

  async getBonusById(id: string): Promise<Bonus> {
    try {
      const response = await authenticatedFetch(
        `${this.apiRoot}/bonuses/${id}`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch bonus: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching bonus ${id}:`, error);
      throw error;
    }
  }

  async createBonus(bonus: BonusCreateEntry): Promise<Bonus> {
    try {
      const response = await authenticatedFetch(`${this.apiRoot}/bonuses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bonus),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        throw new Error(
          error.message || `Failed to create bonus: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating bonus:', error);
      throw error;
    }
  }

  async updateBonus(id: string, bonus: BonusUpdateEntry): Promise<Bonus> {
    try {
      const response = await authenticatedFetch(
        `${this.apiRoot}/bonuses/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bonus),
        }
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        throw new Error(
          error.message || `Failed to update bonus: ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating bonus ${id}:`, error);
      throw error;
    }
  }

  async deleteBonus(id: string): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${this.apiRoot}/bonuses/${id}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        throw new Error(
          error.message || `Failed to delete bonus: ${response.statusText}`
        );
      }
    } catch (error) {
      console.error(`Error deleting bonus ${id}:`, error);
      throw error;
    }
  }
}

export default new BonusService();

